import React from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { WIDGET_REGISTRY, WIDGET_SETTINGS, isWidgetPermitted, getWidgetBaseId } from "./DashboardConfig";
import "./widgetPicker.scss";

const WidgetPicker = ({ open, enabled, settings, onClose, onSave, onAddInstance }) => {
  const [selected, setSelected] = React.useState(enabled);
  const [localSettings, setLocalSettings] = React.useState(settings || {});

  React.useEffect(() => {
    if (open) {
      setSelected(enabled);
      setLocalSettings(settings || {});
    }
  }, [open, enabled, settings]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const setSetting = (widgetId, key, value) => {
    setLocalSettings((prev) => ({
      ...prev,
      [widgetId]: { ...(prev[widgetId] || {}), [key]: value },
    }));
  };

  const removeInstance = (instanceId) => {
    setSelected((prev) => prev.filter((x) => x !== instanceId));
  };

  const permitted = WIDGET_REGISTRY.filter(isWidgetPermitted);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle className="picker-title">Customize Dashboard</DialogTitle>
      <DialogContent className="picker-content">
        <p className="picker-hint">Choose which widgets to show on your dashboard.</p>
        {permitted.map((w) => {
          const widgetSettingsDef = WIDGET_SETTINGS[w.id];

          if (w.multiInstance) {
            // Show instances list + Add button
            const instances = selected.filter((id) => getWidgetBaseId(id) === w.id);
            return (
              <div key={w.id} className="picker-row picker-row--multi">
                <div className="picker-label" style={{ marginBottom: 6 }}>
                  <span className="picker-label__title">{w.title}</span>
                  <span className="picker-label__desc">{w.description}</span>
                </div>
                {instances.length === 0 && (
                  <div className="picker-no-instances">No instances added</div>
                )}
                {instances.map((instanceId) => {
                  const s = localSettings[instanceId] || settings?.[instanceId] || {};
                  const lbl = s.label ||
                    (s.ledgerMeta?.length
                      ? s.ledgerMeta.slice(0, 2).map((m) => m.label).join(", ") + (s.ledgerMeta.length > 2 ? ` +${s.ledgerMeta.length - 2}` : "")
                      : "All ledgers");
                  return (
                    <div key={instanceId} className="picker-instance">
                      <span className="picker-instance__label">{lbl}</span>
                      <button
                        type="button"
                        className="picker-instance__remove"
                        onClick={() => removeInstance(instanceId)}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="picker-add-instance"
                  onClick={() => onAddInstance && onAddInstance(w.id)}
                >
                  + Add {w.title}
                </button>
              </div>
            );
          }

          return (
            <div key={w.id} className="picker-row">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.includes(w.id)}
                    onChange={() => toggle(w.id)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <span className="picker-label">
                    <span className="picker-label__title">{w.title}</span>
                    <span className="picker-label__desc">{w.description}</span>
                  </span>
                }
              />
              {/* Per-widget settings — only shown when the widget is enabled */}
              {widgetSettingsDef && selected.includes(w.id) && (
                <div className="picker-widget-settings">
                  {Object.entries(widgetSettingsDef).map(([key, def]) => (
                    <div key={key} className="picker-setting">
                      <span className="picker-setting__label">{def.label}:</span>
                      <div className="picker-setting__options">
                        {def.options.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            className={[
                              "picker-setting__btn",
                              (localSettings[w.id]?.[key] ?? def.default) === opt.value
                                ? "picker-setting__btn--active"
                                : "",
                            ].join(" ")}
                            onClick={() => setSetting(w.id, key, opt.value)}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </DialogContent>
      <DialogActions className="picker-actions">
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button
          onClick={() => onSave(selected, localSettings)}
          color="primary"
          variant="contained"
          size="small"
          disableElevation
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WidgetPicker;
